import { useEffect, useState } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import { artistListState, songListState } from "../atom/atom";
import { addNewShort } from "../api/write.api";
import { extractYoutubeInfo } from "../api/common.api";
import { Genre, Theme } from "../types/type";
import { fetchYouTubeMetadata, IYoutubeMetaData } from "../tools/tools";
import { generateShortId } from "../api/fetch.api";

interface INewShort {
  title: string;
  youtubeLink: string;
  youtubeId: string;
  thumbnailUrl: string;
  isAI: boolean;
  makerUserId: string;
  originalSongId: string;
  tag: {
    genreList: string[];
    themeList: string[];
    tagList: string[];
    [key: string]: any;
  };
}

function AddShortPage() {
  const [artistId, setArtistId] = useState<string>("");
  const [originalSongId, setOriginalSongId] = useState<string>("");
  const [artistName, setArtistName] = useState<string>("");
  const [genreList, setGenreList] = useState<string[]>([]);
  const [themeList, setThemeList] = useState<string[]>([]);
  const [tagList, setTagList] = useState<string[]>([]);
  const [youtubeMetadata, setYoutubeMetadata] =
    useState<IYoutubeMetaData | null>(null);
  const [newShortList, setNewShortList] = useState<INewShort[]>([
    {
      title: "",
      youtubeLink: "",
      youtubeId: "",
      thumbnailUrl: "",
      isAI: true,
      makerUserId: "Homebrew",
      originalSongId: "",
      tag: { genreList: [], themeList: [], tagList: [] },
    },
  ]);

  const artistList = useRecoilValue(artistListState);
  const totalSongList = useRecoilValue(songListState);

  const handleArtistSelect = (artistId: string, artistName: string) => {
    setArtistId(artistId);
    setArtistName(artistName);
    setNewShortList((prevShortList) =>
      prevShortList.map((short) => ({
        ...short,
        tagList: [artistName],
      }))
    );
  };

  const handleYoutubeLinkChange = async (index: number, link: string) => {
    const { youtubeId, thumbnailUrl } = extractYoutubeInfo(link);
    const metadata = await fetchYouTubeMetadata(link);
    setYoutubeMetadata(metadata);
    setNewShortList((prevShortList) =>
      prevShortList.map((short, i) =>
        i === index
          ? {
              ...short,
              youtubeLink: link,
              youtubeId,
              thumbnailUrl: metadata.thumbnail_url,
              title: metadata.title,
              makerUserId: metadata.author_name,
            }
          : short
      )
    );
  };

  const handleToggleGenre = (index: number, genre: string) => {
    setNewShortList((prevShortList) =>
      prevShortList.map((short, i) => {
        if (i === index) {
          const genreIndex = short.tag.genreList.indexOf(genre);
          if (genreIndex === -1) {
            return {
              ...short,
              tag: {
                ...short.tag,
                genreList: [...short.tag.genreList, genre],
              },
            };
          } else {
            return {
              ...short,
              tag: {
                ...short.tag,
                genreList: short.tag.genreList.filter((g) => g !== genre),
              },
            };
          }
        }
        return short;
      })
    );
    setGenreList((prevList) =>
      prevList.includes(genre)
        ? prevList.filter((g) => g !== genre)
        : [...prevList, genre]
    );
  };

  const handleToggleTheme = (index: number, theme: string) => {
    setNewShortList((prevShortList) =>
      prevShortList.map((short, i) => {
        if (i === index) {
          const themeIndex = short.tag.themeList.indexOf(theme);
          if (themeIndex === -1) {
            return {
              ...short,
              tag: {
                ...short.tag,
                themeList: [...short.tag.themeList, theme],
              },
            };
          } else {
            return {
              ...short,
              tag: {
                ...short.tag,
                themeList: short.tag.themeList.filter((t) => t !== theme),
              },
            };
          }
        }
        return short;
      })
    );
    setThemeList((prevList) =>
      prevList.includes(theme)
        ? prevList.filter((t) => t !== theme)
        : [...prevList, theme]
    );
  };

  const handleInputChange = (
    index: number,
    field: keyof INewShort,
    value: string | boolean
  ) => {
    setNewShortList((prevShortList) =>
      prevShortList.map((short, i) =>
        i === index ? { ...short, [field]: value } : short
      )
    );

    if (field === "originalSongId" && typeof value === "string") {
      const originalSong = totalSongList.find((song) => song.songId === value);
      if (originalSong) {
        setNewShortList((prevShortList) =>
          prevShortList.map((short, i) =>
            i === index
              ? {
                  ...short,
                  tag: {
                    ...short.tag,
                    genreList: Array.from(
                      new Set([
                        ...short.tag.genreList,
                        ...originalSong.tag.genreList,
                      ])
                    ),
                  },
                }
              : short
          )
        );
        setGenreList((prevList) =>
          Array.from(new Set([...prevList, ...originalSong.tag.genreList]))
        );
      }
    }
  };

  const handleAddRow = () => {
    setNewShortList((prevShortList) => [
      ...prevShortList,
      {
        title: "",
        youtubeLink: "",
        youtubeId: "",
        thumbnailUrl: "",
        tagList: artistName ? [artistName] : [],
        isAI: false,
        makerUserId: "Homebrew",
        originalSongId: "",
        tag: {
          genreList: [...genreList],
          themeList: [...themeList],
          tagList: [],
        },
      },
    ]);
  };

  const handleRemoveRow = (index: number) => {
    setNewShortList((prevShortList) =>
      prevShortList.filter((_, i) => i !== index)
    );
  };

  useEffect(() => {
    const test = async () => {
      const shortId = await generateShortId();
      console.log("shortId : " + shortId);
    };
    test();
  }, []);

  const handleAddShort = async () => {
    try {
      await Promise.all(
        newShortList.map((short) =>
          addNewShort({
            artistId,
            artistName,
            ...short,
          })
        )
      );

      // 폼 초기화
      setNewShortList([
        {
          title: "",
          youtubeLink: "",
          youtubeId: "",
          thumbnailUrl: "",
          isAI: newShortList[0].isAI,
          makerUserId: "Homebrew",
          originalSongId: "",
          tag: {
            genreList: [],
            themeList: [...themeList],
            tagList: [],
          },
        },
      ]);
    } catch (error) {
      console.error("Error adding shorts: ", error);
      alert("Failed to add shorts.");
    }
  };

  return (
    <div>
      <h1>쇼츠 추가</h1>
      <div>
        <label>Select Artist:</label>
        {artistList.map((artist) => (
          <button
            key={artist.artistId}
            onClick={() =>
              handleArtistSelect(artist.artistId, artist.artistName)
            }
            style={{
              backgroundColor:
                artist.artistId === artistId ? "lightblue" : "white",
              borderColor: artist.artistId === artistId ? "blue" : "grey",
              borderWidth: 0.1,
              marginRight: 4,
            }}
          >
            {artist.artistName}
          </button>
        ))}
      </div>
      {newShortList.map((short, index) => (
        <div
          key={index}
          style={{ marginTop: "20px", borderBottom: "1px solid #ccc" }}
        >
          <div>
            <label>YouTube Link:</label>
            <input
              type="text"
              value={short.youtubeLink}
              onChange={(e) => handleYoutubeLinkChange(index, e.target.value)}
            />
          </div>
          <div>
            <label>Title:</label>
            <input
              type="text"
              value={short.title}
              onChange={(e) =>
                handleInputChange(index, "title", e.target.value)
              }
            />
          </div>
          <div>
            <label>Thumbnail URL:</label>
            {youtubeMetadata && (
              <img
                src={youtubeMetadata.thumbnail_url}
                width={youtubeMetadata.thumbnail_width}
                height={youtubeMetadata.thumbnail_height}
                alt=""
              />
            )}
            <input type="text" value={short.thumbnailUrl} readOnly />
          </div>
          <div>
            <label>Is AI:</label>
            <input
              type="checkbox"
              checked={short.isAI}
              onChange={() => handleInputChange(index, "isAI", !short.isAI)}
            />
          </div>
          <div>
            <label>Maker User ID:</label>
            <input
              type="text"
              value={short.makerUserId}
              onChange={(e) =>
                handleInputChange(index, "makerUserId", e.target.value)
              }
            />
          </div>
          <div>
            <label>Original Song Id</label>
            <input
              type="text"
              value={short.originalSongId}
              onChange={(e) =>
                handleInputChange(index, "originalSongId", e.target.value)
              }
            />
          </div>
          <div>
            <label>Genre</label>
            {Object.values(Genre).map((genre) => (
              <button
                key={genre}
                onClick={() => handleToggleGenre(index, genre)}
                style={{
                  backgroundColor: short.tag.genreList.includes(genre)
                    ? "lightgreen"
                    : "white",
                  borderColor: short.tag.genreList.includes(genre)
                    ? "green"
                    : "grey",
                  borderWidth: 0.1,
                  marginRight: 4,
                }}
              >
                {genre}
              </button>
            ))}
            <div>
              {short.tag.genreList.map((genre, genreIndex) => (
                <span key={genreIndex} style={{ marginRight: "5px" }}>
                  {genre}
                </span>
              ))}
            </div>
          </div>
          <div>
            <label>Themes</label>
            {Object.keys(Theme).map((theme) => (
              <button
                key={theme}
                onClick={() => handleToggleTheme(index, theme)}
                style={{
                  backgroundColor: short.tag.themeList.includes(theme)
                    ? "lightgreen"
                    : "white",
                  borderColor: short.tag.themeList.includes(theme)
                    ? "green"
                    : "grey",
                  borderWidth: 0.1,
                  marginRight: 4,
                }}
              >
                {theme}
              </button>
            ))}
            <div>
              {short.tag.themeList.map((theme, themeIndex) => (
                <span key={themeIndex} style={{ marginRight: "5px" }}>
                  {theme}
                </span>
              ))}
            </div>
          </div>
          <button type="button" onClick={() => handleRemoveRow(index)}>
            Remove
          </button>
        </div>
      ))}
      <button type="button" onClick={handleAddRow}>
        row 추가
      </button>
      <button type="button" onClick={handleAddShort}>
        데이터 전송
      </button>
    </div>
  );
}

export default AddShortPage;
